import download from 'downloadjs';
import { MimeTypes } from '@/globals/enums';
import axios from 'axios';
import pAll from 'p-all';

/**
 * Download file by URL
 * @param {String} url
 * @param {String} newFileName
 * @param {Boolean} trackProgress
 */
export const downloadFromURL = async (
  url,
  newFileName = undefined,
  trackProgress = false
) => {
  if (newFileName) {
    newFileName = formatClipName(newFileName);
  }
  const fileName = fileNameFromUrl(url);
  const extenstion = fileName
    .substring(fileName.lastIndexOf('.') + 1)
    .toLowerCase();
  const mimeType = MimeTypes[extenstion];
  if (mimeType) {
    try {
      const response = await axios({
        url: url,
        method: 'GET',
        responseType: 'blob',
      });
      download(response.data, newFileName || fileName, mimeType);
      // For tracking purpose, use trackProgress params
      if (trackProgress) {
        if (response.status === 200 && response.statusText === 'OK') {
          return { response, fileName };
        } else {
          return null;
        }
      }
    } catch (error) {
      console.log(error);
      return null;
    }
  }
};

/**
 * Get file name from url
 * @param {String} url
 * @returns file name
 */
export const fileNameFromUrl = (url) => {
  // eslint-disable-next-line no-useless-escape
  var matches = url.match(/\/([^\/?#]+)[^\/]*$/);
  if (matches.length > 1) {
    return matches[1];
  }
  return null;
};

/**
 * Format clip name
 * @param {String} clipName
 * @returns String name
 * The following strings should be converted to "_".
 * --------------------
 *   < (less than)
 *   > (greater than)
 *   : (colon)
 *   " (double quote)
 *   / (forward slash)
 *   \ (backslash)
 *   | (vertical bar or pipe)
 *   ? (question mark)
 *   * (asterisk)
 * --------------------
 * The following strings should also be converted to "_".
 * --------------------
 *   Empty {clipName}
 *   Dot "." at the beginning of {clipName}
 * --------------------
 * In addition, if name exceeds 100 characters, cut off the characters after 100.
 */
export const formatClipName = (clipName) => {
  let name = clipName;
  // Handle empty input
  if (!name.trim()) {
    return '_';
  }
  // Convert special characters to "_"
  const specialCharacters = /[<>:"/\\|?*]/g;
  name = name.replace(specialCharacters, '_');
  // Replace dot at the beginning
  if (name.startsWith('.')) {
    name = '_' + name.slice(1);
  }
  return name;
};

/**
 * Cut clip name
 * Keep part numbers (optional)
 * @param {String} clipName
 * @param {Boolean} namingIndex
 * @returns String name
 */
export const cutClipName = (clipName, namingIndex) => {
  let name = clipName;
  const maxLength = 100;
  if (name.length > maxLength && namingIndex) {
    const parts = name.match(/^(.*?)(-\d+$)/);
    const [, firstPart, orderPart] = parts;
    const newMaxLength = maxLength - orderPart.length;
    const trimmedFirstPart =
      firstPart.length > newMaxLength
        ? firstPart.slice(0, newMaxLength)
        : firstPart;
    name = `${trimmedFirstPart}${orderPart}`;
  } else {
    name = name.slice(0, maxLength);
  }
  return name;
};

/**
 * Download video clips sequentially
 * Processes each clip individually, gets Signed URLs immediately before downloading
 * @param {Array} selectedClips - Array of clip objects with id and clipName
 * @param {Function} downloadVideoClipsService - Service function to get download URLs
 * @returns {Promise<Array>} Array of successfully downloaded file names
 */
export const downloadVideoClipsSequentially = async (
  selectedClips,
  downloadVideoClipsService
) => {
  const downloadedFiles = [];

  for (const clip of selectedClips) {
    try {
      // get signed URL for a clip
      const res = await downloadVideoClipsService(clip.id);
      if (res && res.archives && res.archives.length) {
        const archive = res.archives[0];

        const namingIndex = archive.urls.length > 1;
        const fileTasks = [];

        // create download tasks for all files of this clip
        archive.urls.forEach((urlObject, index) => {
          fileTasks.push(async () => {
            try {
              const url = urlObject.url;

              if (!url || downloadedFiles.includes(fileNameFromUrl(url))) {
                return;
              }

              const indexedName =
                clip.clipName + (namingIndex ? `-${index + 1}` : '');
              const finalName = cutClipName(indexedName, namingIndex);

              // download file from signed URL
              const downloadResult = await downloadFromURL(
                url,
                finalName,
                true
              );

              if (downloadResult) {
                downloadedFiles.push(downloadResult.fileName);
              } else {
                console.warn(`Failed to download file: ${url}`);
              }
            } catch (error) {
              console.log(`Failed to download file: ${error}`);
            }
          });
        });

        // wait for all files of this clip to finish downloading
        await pAll(fileTasks, { concurrency: 1 });
      }
    } catch (error) {
      console.log(`Failed to get URLs for clip ${clip.id}: ${error}`);
    }
  }

  return downloadedFiles;
};
